import { useState, useMemo } from 'react';
import {
    SearchLg,
    Eye,
    Users01,
    Package,
    User01,
    Plus,
    CheckVerified01,
} from '@untitledui/icons';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { Table, TableCard } from '@/components/application/table/table';
import { Badge } from '@/components/base/badges/badges';
import { Dropdown } from '@/components/base/dropdown/dropdown';
import { useLocation, useNavigate } from 'react-router';
import { navigationStateFrom } from '@/types/navigation';
import { getPartners } from '@/data/api/receivers';
import type { Partner } from '@/types/receiver';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useMe } from '@/hooks/api/use-auth';
import { Button } from '@/components/base/buttons/button';
import { EmptyState } from '@/components/application/empty-state/empty-state';

// Company type badge colors
const getCompanyTypeBadgeColor = (type: string | undefined) => {
    switch (type?.toLowerCase()) {
        case 'receiver':
            return 'brand';
        case 'freight_forwarder':
            return 'success';
        case 'carrier':
            return 'warning';
        case 'customs_broker':
            return 'error';
        case 'sender':
            return 'gray';
        default:
            return 'gray';
    }
};

// Format company type for display
const formatCompanyType = (type: string | undefined) => {
    if (!type) return '-';
    return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

export const ShipperBusinessPartnersPage = () => {
    const { t } = useTranslation(['pages:receivers', 'common']);
    const location = useLocation();
    const navigate = useNavigate();
    const { data: currentUser } = useMe();
    const [searchTerm, setSearchTerm] = useState('');
    const [countryFilter, setCountryFilter] = useState('');
    const [companyTypeFilter, setCompanyTypeFilter] = useState('');

    // Fetch business partners (all types: receivers, carriers, freight forwarders, etc.)
    const { data: partners = [], isLoading } = useQuery<Partner[]>({
        queryKey: ['business-partners', currentUser?.company_id],
        queryFn: async () => {
            if (!currentUser?.company_id) {
                return [];
            }
            return await getPartners(currentUser.company_id);
        },
        enabled: !!currentUser?.company_id,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });

    // Extract unique countries for filter
    const countryOptions = useMemo(() => {
        const countries = new Set<string>();
        partners.forEach((partner) => {
            if (partner.country) {
                countries.add(partner.country);
            }
        });
        const countryArray = Array.from(countries).sort();
        return [
            { id: '', label: t('list.allCountries', { ns: 'pages:receivers' }) },
            ...countryArray.map((country) => ({ id: country, label: country })),
        ];
    }, [partners, t]);

    // Extract unique company types for filter
    const companyTypeOptions = useMemo(() => {
        const types = new Set<string>();
        partners.forEach((partner) => {
            if (partner.company_type) {
                types.add(partner.company_type);
            }
        });
        const typeArray = Array.from(types).sort();
        return [
            { id: '', label: t('list.allTypes', { defaultValue: 'All Types' }) },
            ...typeArray.map((type) => ({ id: type, label: formatCompanyType(type) })),
        ];
    }, [partners, t]);

    // Filter partners
    const filteredPartners = useMemo(() => {
        return partners.filter((partner) => {
            const matchesSearch = !searchTerm ||
                partner.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                partner.legal_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                partner.primary_email?.toLowerCase().includes(searchTerm.toLowerCase());

            const matchesCountry = !countryFilter || partner.country === countryFilter;
            const matchesType = !companyTypeFilter || partner.company_type === companyTypeFilter;

            return matchesSearch && matchesCountry && matchesType;
        });
    }, [partners, searchTerm, countryFilter, companyTypeFilter]);

    // Navigate to partner detail
    const handleRowClick = (partnerId: string) => {
        navigate(`/shipper/business-partners/${partnerId}`, { state: navigationStateFrom(location) });
    };

    // Navigate to address book filtered by company (ID-based)
    const handleSeePeople = (companyId: string, companyName: string) => {
        navigate(`/shipper/address-book?contact_company_id=${companyId}&company=${encodeURIComponent(companyName)}`, { state: navigationStateFrom(location) });
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-lg text-tertiary">{t('list.loading')}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-primary">
                        Business Partners
                    </h1>
                    <p className="text-tertiary mt-1">
                        Manage your business partners and their relationships
                    </p>
                </div>
                <Button
                    onClick={() => navigate('/shipper/business-partners/new')}
                    color="primary"
                    size="md"
                    iconLeading={Plus}
                >
                    Add Business Partner
                </Button>
            </div>

            {/* Filters */}
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <Input
                        placeholder={t('list.searchPlaceholder', { ns: 'pages:receivers' })}
                        value={searchTerm}
                        onChange={setSearchTerm}
                        icon={SearchLg}
                        size="md"
                    />

                    <Select
                        placeholder={t('list.countryPlaceholder', { ns: 'pages:receivers' })}
                        items={countryOptions}
                        selectedKey={countryFilter}
                        onSelectionChange={(key) => setCountryFilter(key as string)}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>

                    <Select
                        placeholder={t('list.typePlaceholder', { defaultValue: 'Company Type' })}
                        items={companyTypeOptions}
                        selectedKey={companyTypeFilter}
                        onSelectionChange={(key) => setCompanyTypeFilter(key as string)}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>
                </div>
            </div>

            {/* Partners Table or Empty State */}
            {filteredPartners.length > 0 ? (
                <TableCard.Root>
                    <TableCard.Header
                        title={`${t('list.title', { defaultValue: 'Business Partners' })} (${filteredPartners.length})`}
                        description={t('list.description', { ns: 'pages:receivers', defaultValue: 'Your business partners and their container activity' })}
                    />

                    <Table>
                        <Table.Header>
                            <Table.Head label={t('list.headers.company')} isRowHeader />
                            <Table.Head label={t('list.headers.type', { defaultValue: 'Type' })} />
                            <Table.Head label={t('list.headers.country')} />
                            <Table.Head label={t('list.headers.activeContainers')} />
                            <Table.Head label={t('list.headers.totalContainers')} />
                            <Table.Head label={t('list.headers.lastContainer')} />
                            <Table.Head label={t('list.headers.actions')} />
                        </Table.Header>
                        <Table.Body>
                            {filteredPartners.map((partner) => (
                                <Table.Row
                                    key={partner.company_id}
                                    onClick={() => handleRowClick(partner.company_id)}
                                    className="cursor-pointer hover:bg-secondary transition-colors"
                                >
                                    <Table.Cell>
                                        <div className="flex items-center gap-1.5">
                                            <span className="font-medium text-primary">
                                                {partner.legal_name || partner.company_name}
                                            </span>
                                            {partner.is_verified && (
                                                <CheckVerified01 className="size-4 text-success-600" />
                                            )}
                                        </div>
                                        {partner.primary_email && (
                                            <div className="text-sm text-tertiary">{partner.primary_email}</div>
                                        )}
                                    </Table.Cell>
                                    <Table.Cell>
                                        <Badge
                                            color={getCompanyTypeBadgeColor(partner.company_type)}
                                            size="sm"
                                        >
                                            {formatCompanyType(partner.company_type)}
                                        </Badge>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <div className="flex items-center gap-2">
                                            <Package className="size-4 text-tertiary" />
                                            <span className="text-sm text-tertiary">{partner.country || '-'}</span>
                                        </div>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <div className="flex items-center gap-2">
                                            <Package className="size-4 text-tertiary" />
                                            <span className="text-sm text-primary">{partner.activeContainers}</span>
                                        </div>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <span className="text-sm text-tertiary">{partner.totalContainers}</span>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <span className="text-sm text-tertiary">
                                            {partner.lastContainerDate
                                                ? new Date(partner.lastContainerDate).toLocaleDateString('it-IT')
                                                : t('list.none')}
                                        </span>
                                    </Table.Cell>
                                    <Table.Cell onClick={(e) => e.stopPropagation()}>
                                        <Dropdown.Root>
                                            <Dropdown.DotsHorizontalButton />
                                            <Dropdown.Popover className="w-min">
                                                <Dropdown.Menu>
                                                    <Dropdown.Item
                                                        icon={Eye}
                                                        label={t('list.actions.viewDetails')}
                                                        onAction={() => handleRowClick(partner.company_id)}
                                                    />
                                                    <Dropdown.Item
                                                        icon={User01}
                                                        label={t('list.actions.seePeople', { defaultValue: 'See People' })}
                                                        onAction={() => handleSeePeople(partner.company_id, partner.company_name)}
                                                    />
                                                </Dropdown.Menu>
                                            </Dropdown.Popover>
                                        </Dropdown.Root>
                                    </Table.Cell>
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table>
                </TableCard.Root>
            ) : (
                <EmptyState size="lg">
                    <EmptyState.Header>
                        <EmptyState.FeaturedIcon icon={Users01} color="gray" theme="modern" />
                    </EmptyState.Header>
                    <EmptyState.Content>
                        <EmptyState.Title>
                            {searchTerm || countryFilter || companyTypeFilter
                                ? t('list.noResults', { defaultValue: 'No business partners found' })
                                : t('list.empty', { defaultValue: 'No business partners yet' })}
                        </EmptyState.Title>
                        <EmptyState.Description>
                            {searchTerm || countryFilter || companyTypeFilter
                                ? t('list.noResultsDescription', { defaultValue: 'Try adjusting your filters to find other partners.' })
                                : t('list.emptyDescription', { defaultValue: 'This section will populate automatically as you add containers with associated partners.' })}
                        </EmptyState.Description>
                    </EmptyState.Content>
                </EmptyState>
            )}
        </div>
    );
};
